import React, { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AdminDashboard = () => {
  const { api, user } = useAuth();
  const [stats, setStats] = useState({
    today: { page_views: 0, unique_visitors: 0, signatures_count: 0 },
    total_signatures: 0,
    month: { total_views: 0, total_visitors: 0, total_signatures: 0 },
    recent_signatures: [],
    daily_chart: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.statistics.getDashboard();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error("Error loading dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const chartData = {
    labels: stats.daily_chart.map((item) =>
      new Date(item.visit_date).toLocaleDateString("th-TH", {
        day: "numeric",
        month: "short",
      })
    ),
    datasets: [
      {
        label: "การเข้าชม",
        data: stats.daily_chart.map((item) => item.page_views),
        borderColor: "#007CC7",
        backgroundColor: "rgba(0, 124, 199, 0.1)",
        tension: 0.4,
      },
      {
        label: "ผู้เข้าชม",
        data: stats.daily_chart.map((item) => item.unique_visitors),
        borderColor: "#4DA8DA",
        backgroundColor: "rgba(77, 168, 218, 0.1)",
        tension: 0.4,
      },
    ],
  };

  const barChartData = {
    labels: stats.daily_chart.map((item) =>
      new Date(item.visit_date).toLocaleDateString("th-TH", {
        day: "numeric",
        month: "short",
      })
    ),
    datasets: [
      {
        label: "จำนวนลงนาม",
        data: stats.daily_chart.map((item) => item.signatures_count),
        backgroundColor: "rgba(0, 124, 199, 0.8)",
        borderColor: "#007CC7",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">กำลังโหลดข้อมูล...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard py-4">
      <div className="container-fluid">
        {/* Welcome Section */}
        <div className="mb-4">
          <h1 className="h3 mb-0">
            <i className="fas fa-tachometer-alt me-2"></i>
            แดชบอร์ด
          </h1>
          <p className="text-muted mb-0">
            ยินดีต้อนรับ, {user?.fullname} (
            {user?.role === "super_admin" ? "Super Admin" : "Admin"})
          </p>
        </div>

        {/* Info Boxes */}
        <div className="dashboard-grid mb-4">
          <div className="info-box">
            <div className="info-box-icon bg-gradient-primary">
              <i className="fas fa-eye"></i>
            </div>
            <div className="info-box-content">
              <h3>{stats.today.page_views.toLocaleString()}</h3>
              <p>การเข้าชมวันนี้</p>
            </div>
          </div>

          <div className="info-box">
            <div className="info-box-icon bg-gradient-success">
              <i className="fas fa-users"></i>
            </div>
            <div className="info-box-content">
              <h3>{stats.today.unique_visitors.toLocaleString()}</h3>
              <p>ผู้เข้าชมวันนี้</p>
            </div>
          </div>

          <div className="info-box">
            <div className="info-box-icon bg-gradient-warning">
              <i className="fas fa-signature"></i>
            </div>
            <div className="info-box-content">
              <h3>{stats.today.signatures_count.toLocaleString()}</h3>
              <p>ลงนามวันนี้</p>
            </div>
          </div>

          <div className="info-box">
            <div className="info-box-icon bg-gradient-danger">
              <i className="fas fa-book"></i>
            </div>
            <div className="info-box-content">
              <h3>{stats.total_signatures.toLocaleString()}</h3>
              <p>ลงนามทั้งหมด</p>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="row mb-4">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-header card-header-gradient">
                <h5 className="mb-0">
                  <i className="fas fa-chart-line me-2"></i>
                  สถิติการเข้าชม (7 วันล่าสุด)
                </h5>
              </div>
              <div className="card-body">
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card">
              <div className="card-header card-header-gradient">
                <h5 className="mb-0">
                  <i className="fas fa-chart-bar me-2"></i>
                  จำนวนลงนาม
                </h5>
              </div>
              <div className="card-body">
                <Bar data={barChartData} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Stats */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header card-header-gradient">
                <h5 className="mb-0">
                  <i className="fas fa-calendar-alt me-2"></i>
                  สรุปข้อมูลเดือนนี้
                </h5>
              </div>
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-md-4">
                    <div className="p-3">
                      <h2 className="text-primary">
                        {stats.month?.total_views?.toLocaleString() || 0}
                      </h2>
                      <p className="text-muted mb-0">การเข้าชมทั้งหมด</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3">
                      <h2 className="text-success">
                        {stats.month?.total_visitors?.toLocaleString() || 0}
                      </h2>
                      <p className="text-muted mb-0">ผู้เข้าชมทั้งหมด</p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3">
                      <h2 className="text-warning">
                        {stats.month?.total_signatures?.toLocaleString() || 0}
                      </h2>
                      <p className="text-muted mb-0">จำนวนลงนาม</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Signatures */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header card-header-gradient">
                <h5 className="mb-0">
                  <i className="fas fa-clock me-2"></i>
                  การลงนามล่าสุด
                </h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>ชื่อ-นามสกุล</th>
                        <th>หน่วยงาน</th>
                        <th>จังหวัด</th>
                        <th>ข้อความ</th>
                        <th>วันที่</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recent_signatures.map((sig, index) => (
                        <tr key={sig.id}>
                          <td>{index + 1}</td>
                          <td>{sig.fullname}</td>
                          <td>{sig.organization || "-"}</td>
                          <td>{sig.province || "-"}</td>
                          <td>
                            <span
                              className="text-truncate d-inline-block"
                              style={{ maxWidth: "200px" }}
                              title={sig.message}
                            >
                              {sig.message}
                            </span>
                          </td>
                          <td>
                            {new Date(sig.created_at).toLocaleDateString(
                              "th-TH",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </td>
                        </tr>
                      ))}
                      {stats.recent_signatures.length === 0 && (
                        <tr>
                          <td colSpan="6" className="text-center text-muted">
                            ยังไม่มีการลงนาม
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
